import { z } from 'zod';
import { emailSchema, slugSchema, uuidSchema, visuelSchema } from './common.js';
import { ROLES } from '../permissions/permissions.js';
/**
 * Schémas d'authentification, partagés par le formulaire de connexion et par
 * la validation du corps de requête côté serveur.
 */
/**
 * Politique de mot de passe : 10 caractères minimum.
 *
 * La longueur prime sur la complexité imposée : exiger majuscule + chiffre +
 * symbole pousse à des mots de passe courts et prévisibles (« Passw0rd! »),
 * alors qu'une phrase longue résiste mieux. Recommandation NIST SP 800-63B.
 */
export const passwordSchema = z
    .string()
    .min(10, 'Au moins 10 caractères')
    .max(200, 'Au plus 200 caractères');
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Mot de passe requis'),
    /**
     * L'adresse de la BOUTIQUE, quand on se connecte par elle.
     *
     * ⭐ Présente, le mot de passe est vérifié dans le schéma de cette boutique —
     * c'est là que vivent les comptes de l'équipe. Absente, on cherche dans la
     * table commune : les administrateurs de boutique et de plateforme y restent,
     * et c'est par eux qu'on entre quand un schéma n'existe pas encore.
     */
    storeSlug: slugSchema.optional(),
    /** UUIDv7 de l'appareil, généré côté client et conservé en local. */
    deviceId: uuidSchema.optional(),
    deviceLabel: z.string().max(120).optional(),
});
export const forgotPasswordSchema = z.object({ email: emailSchema });
export const resetPasswordSchema = z.object({
    token: z.string().min(20),
    password: passwordSchema,
});
export const acceptInviteSchema = z.object({
    token: z.string().min(20),
    password: passwordSchema,
    firstName: z.string().min(1).max(80),
    lastName: z.string().min(1).max(80),
});
/**
 * Modele de commerce.
 *
 * ⭐ Ce n'est PAS une categorie decorative : il fixe les valeurs par defaut du
 * catalogue au moment ou la boutique nait — unite de vente et suivi de stock.
 * Un restaurant qui doit decocher « suivre le stock » sur chaque plat abandonne
 * au troisieme ; un revendeur d'electronique a qui l'on propose « heure » comme
 * unite se demande ou il est tombe.
 *
 * Il reste modifiable ensuite : c'est une amorce, pas un enfermement.
 */
export const BUSINESS_MODELS = [
    {
        code: 'retail',
        label: 'Commerce de détail',
        description: 'Vente de produits en boutique — électronique, prêt-à-porter, épicerie.',
        defaultUnit: 'piece',
        trackStock: true,
    },
    {
        code: 'restauration',
        label: 'Restauration',
        description: 'Plats et boissons servis sur place ou à emporter.',
        defaultUnit: 'piece',
        trackStock: false,
    },
    {
        code: 'services',
        label: 'Services',
        description: 'Prestations facturées au temps ou au forfait — conseil, coiffure, entretien.',
        defaultUnit: 'hour',
        trackStock: false,
    },
    {
        code: 'atelier',
        label: 'Atelier / réparation',
        description: 'Main-d’œuvre et pièces détachées sur un même document.',
        defaultUnit: 'piece',
        trackStock: true,
    },
    {
        code: 'beaute',
        label: 'Salon de beauté',
        description: 'Coiffure, esthétique, soins — prestations sur rendez-vous et produits revendus.',
        defaultUnit: 'piece',
        trackStock: true,
    },
    {
        code: 'grossiste',
        label: 'Négoce / gros',
        description: 'Vente en quantité, souvent au poids ou au volume.',
        defaultUnit: 'kg',
        trackStock: true,
    },
];
export const businessModelSchema = z.enum(BUSINESS_MODELS.map((m) => m.code));
export const createStoreSchema = z.object({
    businessModel: businessModelSchema.default('retail'),
    id: uuidSchema.optional(),
    name: z.string().min(2).max(120),
    slug: slugSchema,
    countryCode: z.string().length(2).toUpperCase(),
    legalName: z.string().max(160).optional(),
    /** Adresse POSTALE de la boutique — a ne pas confondre avec le slug. */
    address: z.string().trim().max(240).optional(),
    phone: z.string().trim().max(30).optional(),
    /**
     * ⚠️ Validées mais FACULTATIVES, et vides plutôt qu'absentes quand on les
     * efface : un commerçant qui vide le champ veut retirer la mention du ticket,
     * pas garder l'ancienne.
     */
    email: z
        .string()
        .trim()
        .max(160)
        .email('Adresse électronique invalide')
        .optional()
        .or(z.literal('')),
    website: z.string().trim().max(200).optional().or(z.literal('')),
    /** Modules actifs des le depart ; a defaut, ceux du modele de commerce. */
    modules: z.record(z.string(), z.boolean()).optional(),
    /**
     * Logo encode en data-URI, deja redimensionne par le navigateur.
     *
     * ⚠️ Le plafond vient de `visuelSchema`, comme pour les photos d'article et
     * les portraits : le logo sort du MÊME redimensionnement, il n'y a aucune
     * raison qu'il soit tenu à un sixième de leur poids. La création et la mise
     * à jour d'une boutique doivent en outre s'accorder — sinon un logo passe à
     * la création et se fait refuser à la première modification, ou l'inverse.
     */
    logoUrl: visuelSchema
        .regex(/^data:image\/(jpeg|png|webp);base64,/, "Format d'image non pris en charge")
        .optional(),
    taxId: z.string().max(60).optional(),
    /**
     * Administrateur de la boutique — FACULTATIF.
     *
     * Sans lui, c'est le createur (administrateur de plateforme) qui devient
     * administrateur de la boutique : une boutique sans personne pour la gerer
     * est une impasse dont on ne sort qu'en base.
     */
    adminEmail: emailSchema.optional(),
    adminFirstName: z.string().trim().max(80).optional(),
    adminLastName: z.string().trim().max(80).optional(),
});
export const roleSchema = z.enum(ROLES);
/** Ce que le serveur renvoie après authentification. */
/**
 * Modules de l'application, tels qu'ils apparaissent dans le menu lateral.
 *
 * ⭐ TOUS sont desactivables, sans exception. Le reglage vit dans la console
 * PLATEFORME, pas dans la boutique : couper « Parametres » n'enferme donc
 * personne, puisqu'on le reactive depuis l'exterieur. C'est ce qui rend
 * l'absence de verrou sans danger — la meme liberte serait piegeuse si le
 * reglage se trouvait a l'interieur de la boutique.
 */
export const STORE_MODULES = [
    { code: 'dashboard', label: 'Tableau de bord' },
    { code: 'clients', label: 'Clients' },
    { code: 'catalogue', label: 'Catalogue' },
    { code: 'ventes', label: 'Ventes' },
    { code: 'depenses', label: 'Dépenses courantes' },
    { code: 'facturation', label: 'Facturation' },
    { code: 'agenda', label: 'Agenda' },
    { code: 'taches', label: 'Tâches' },
    { code: 'campagnes', label: 'Campagnes marketing' },
    { code: 'rapports', label: 'Rapports' },
    { code: 'utilisateurs', label: 'Utilisateurs' },
    { code: 'parametres', label: 'Paramètres' },
];
/**
 * Modules actifs par defaut selon le metier.
 *
 * Un salon n'a que faire de la facturation fournisseurs le premier jour, mais
 * l'agenda lui est vital ; un negociant, l'inverse. Ce ne sont que des valeurs
 * de depart, modifiables dans les reglages.
 */
export function modulesParDefaut(modele) {
    const tous = Object.fromEntries(STORE_MODULES.map((m) => [m.code, true]));
    if (modele === 'services' || modele === 'beaute')
        return { ...tous, catalogue: true };
    if (modele === 'grossiste' || modele === 'retail')
        return { ...tous, agenda: false };
    return tous;
}
//# sourceMappingURL=auth.js.map